import { SelectOption } from '@shape-ds/components';
import { useGetCaseOptionsQuery } from 'queries/cases/queries';
import { CaseForm, CaseStatus, caseStatus, CaseStatusEnum } from 'queries/cases/types';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { statusRules } from './utils/stageValidation.helper';

export const useGetSelectOptions = () => {
  const { t } = useTranslation();
  const { data } = useGetCaseOptionsQuery();
  const {
    getValues,
    formState: { isValid },
  } = useFormContext<CaseForm>();
  const currentStatus = getValues('status') ?? caseStatus.diagnosis;

  const caseOptions = Object.keys(data?.status ?? []).sort((a, b) => {
    const aValue = CaseStatusEnum[a as keyof typeof CaseStatusEnum];
    const bValue = CaseStatusEnum[b as keyof typeof CaseStatusEnum];

    if (aValue === 0) return 1;
    if (bValue === 0) return -1;

    if (aValue > bValue) return 1;
    if (bValue > aValue) return -1;

    return 0;
  }) as CaseStatus[];

  const statusIsDisabled = useCallback(
    (option: CaseStatus): boolean => {
      if (option === currentStatus) return false;
      if (option !== caseStatus.closed && currentStatus === caseStatus.closed) return true;

      return statusRules[option]({
        isCurrentStatus: option === currentStatus,
        getValues,
        currentStatus,
      });
    },
    [currentStatus, getValues],
  );

  const formattedOptions: SelectOption[] = useMemo(
    () =>
      caseOptions.map((option) => ({
        label: t(`case:status.${option}`),
        value: option,
        disabled: statusIsDisabled(option) || (!isValid && option !== caseStatus.diagnosis),
        isBlocked: statusIsDisabled(option) || (!isValid && option !== caseStatus.diagnosis),
      })),
    [caseOptions, statusIsDisabled, t, isValid],
  );

  return {
    formattedOptions,
  };
};
